import type { AlphaEvalReport } from '../types/gateway';
import { useTranslation } from '../i18n/useTranslation';

interface AlphaEvalPanelProps {
  report: AlphaEvalReport;
  className?: string;
}

type MetricFormat = 'pct' | 'ratio' | 'count' | 'raw';

interface MetricDef {
  key: string;
  label: string;
  format: MetricFormat;
  good?: 'high' | 'low';
}

const CORE_METRICS: MetricDef[] = [
  { key: 'sharpe', label: 'Sharpe', format: 'ratio', good: 'high' },
  { key: 'win_rate', label: 'Win Rate', format: 'pct', good: 'high' },
  { key: 'profit_factor', label: 'PF', format: 'ratio', good: 'high' },
  { key: 'max_drawdown', label: 'MDD', format: 'pct', good: 'low' },
  { key: 'calmar', label: 'Calmar', format: 'ratio', good: 'high' },
];

const ADVANCED_METRICS: MetricDef[] = [
  { key: 'sortino', label: 'Sortino', format: 'ratio', good: 'high' },
  { key: 'var_95', label: 'VaR 95', format: 'pct', good: 'low' },
  { key: 'cvar_95', label: 'CVaR 95', format: 'pct', good: 'low' },
  { key: 'lo_sharpe', label: 'Lo-Sharpe', format: 'ratio', good: 'high' },
];

const REGIME_COLORS: Record<string, string> = {
  bull: 'text-success',
  bear: 'text-danger',
  sideways: 'text-warning',
};

const REGIME_BG: Record<string, string> = {
  bull: 'bg-success/10 border-success/30',
  bear: 'bg-danger/10 border-danger/30',
  sideways: 'bg-warning/10 border-warning/30',
};

function formatMetric(value: number, format: MetricFormat): string {
  switch (format) {
    case 'pct': return `${(value * 100).toFixed(1)}%`;
    case 'ratio': return value.toFixed(2);
    case 'count': return value.toLocaleString();
    case 'raw': return value.toFixed(4);
  }
}

function metricColor(value: number, good?: 'high' | 'low'): string {
  if (!good) return 'text-foreground';
  if (good === 'high') return value > 0 ? 'text-success' : 'text-danger';
  return value < 0 ? 'text-success' : 'text-danger';
}

function MetricRow({ def, value }: { def: MetricDef; value: number | undefined }) {
  if (value === undefined) return null;
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[9px] font-mono text-muted-foreground">{def.label}</span>
      <span className={`text-[10px] font-mono font-medium ${metricColor(value, def.good)}`}>
        {formatMetric(value, def.format)}
      </span>
    </div>
  );
}

export function AlphaEvalPanel({ report, className = '' }: AlphaEvalPanelProps) {
  const { t } = useTranslation();
  const gateEntries = Object.entries(report.floor_gates);
  const passCount = gateEntries.filter(([, g]) => g.pass).length;

  return (
    <div className={`bg-card rounded border border-border flex flex-col ${className}`}>
      <div className="drag-handle h-6 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">{t('alphaeval.header')}</span>
        <span className="ml-auto text-[9px] font-mono text-muted-foreground">{report.combo}</span>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-2">
        {/* Floor Gates */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <span className="text-[8px] font-mono text-muted uppercase tracking-widest">FLOOR GATES</span>
            <span className={`text-[8px] font-mono ${passCount === gateEntries.length ? 'text-success' : 'text-warning'}`}>
              {passCount}/{gateEntries.length}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {gateEntries.map(([key, gate]) => (
              <div
                key={key}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${
                  gate.pass ? 'border-success/20 bg-success/5' : 'border-danger/20 bg-danger/5'
                }`}
              >
                <span className={`status-pip ${gate.pass ? 'status-pip--active' : 'status-pip--killed'}`} />
                <span className="text-[8px] font-mono text-muted-foreground flex-1 truncate">{key}</span>
                <span className={`text-[8px] font-mono ${gate.pass ? 'text-success' : 'text-danger'}`}>
                  {typeof gate.value === 'number' ? gate.value.toFixed(2) : gate.value ? 'PASS' : 'FAIL'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Core Metrics */}
        <div>
          <span className="text-[8px] font-mono text-muted uppercase tracking-widest">CORE</span>
          <div className="mt-0.5">
            {CORE_METRICS.map((def) => (
              <MetricRow key={def.key} def={def} value={report.core_metrics[def.key]} />
            ))}
            {Object.entries(report.core_metrics)
              .filter(([key]) => !CORE_METRICS.some((d) => d.key === key))
              .map(([key, val]) => (
                <MetricRow key={key} def={{ key, label: key, format: 'ratio' }} value={val} />
              ))}
          </div>
        </div>

        {/* Advanced Metrics */}
        <div>
          <span className="text-[8px] font-mono text-muted uppercase tracking-widest">ADVANCED</span>
          <div className="mt-0.5">
            {ADVANCED_METRICS.map((def) => (
              <MetricRow key={def.key} def={def} value={report.advanced_metrics[def.key]} />
            ))}
            {Object.entries(report.advanced_metrics)
              .filter(([key]) => !ADVANCED_METRICS.some((d) => d.key === key))
              .map(([key, val]) => (
                <MetricRow key={key} def={{ key, label: key, format: 'ratio' }} value={val} />
              ))}
          </div>
        </div>

        {/* Per-Regime Breakdown */}
        <div>
          <span className="text-[8px] font-mono text-muted uppercase tracking-widest">REGIME</span>
          <div className="flex gap-1 mt-1">
            {Object.entries(report.per_regime).map(([regime, data]) => (
              <div
                key={regime}
                className={`flex-1 px-1.5 py-1 rounded-sm border ${REGIME_BG[regime] ?? 'border-border bg-card-elevated'}`}
              >
                <div className={`text-[8px] font-mono font-bold uppercase ${REGIME_COLORS[regime] ?? 'text-foreground'}`}>
                  {regime}
                </div>
                <div className="text-[8px] font-mono text-muted-foreground mt-0.5 space-y-0.5">
                  <div>S {data.sharpe.toFixed(1)}</div>
                  <div>WR {(data.wr * 100).toFixed(0)}%</div>
                  <div>IC {data.ic.toFixed(3)}</div>
                  <div>{data.trades}T</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
